import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { InstallBotDialog } from '@components/Bots/InstallBotDialog'
import LoginComponent from '@components/Login/LoginComponent'
import { useEthToUsdFormatted } from '@components/Web3/useEthPrice'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FancyButton, Paragraph, Stack } from '@ui'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import {
    blockchainQueryKeys,
    type BotData,
    useBotStatus,
    useBotStatusPrefetch,
    useConnectivity,
    useTownsContext,
} from 'use-towns-client'
import { isTownBanned } from 'utils'

import { PATHS } from '../index'

const INSTALL_BOT_PARAM = 'install-bot'
const PRIVY_OAUTH_STATE = 'privy_oauth_state'

export function InstallBotBottomBar({ bot }: { bot: BotData }) {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { isAuthenticated } = useConnectivity()
    const [showInstallDialog, setShowInstallDialog] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams()

    const hasPendingInstall =
        searchParams.has(PRIVY_OAUTH_STATE) && searchParams.has(INSTALL_BOT_PARAM)

    const { data: aaAddress } = useMyAbstractAccountAddress()
    const { spaces } = useTownsContext()
    const { enableBotDm } = useFeatureFlags()

    const userOwnsAnySpaces = useMemo(
        () =>
            !!aaAddress &&
            spaces.some((space) => {
                if (space.owner !== aaAddress) {
                    return false
                }
                if (isTownBanned(space.address)) {
                    return false
                }
                return true
            }),
        [spaces, aaAddress],
    )

    const { data: botStatus } = useBotStatus(bot?.clientAddress)
    const prefetchBotStatus = useBotStatusPrefetch()
    const isBotDisabled = botStatus?.isActive === false

    const cannotUseBot = !userOwnsAnySpaces && !enableBotDm
    const isBotUnavailable = isBotDisabled || cannotUseBot

    const isBotFree = bot && bot.installPrice <= 0n
    const priceInUsd = useEthToUsdFormatted({ ethAmount: bot?.installPrice })
    const buttonText = isBotFree ? 'Use Bot' : `Use Bot for ${priceInUsd}`

    // with react-router framework / tanstack start
    // this could be guaranteed in the route level fetch (as a dependency)
    const { mutate: navigateToDm, isPending: isNavigatingToDm } = useMutation({
        mutationFn: async () => {
            if (aaAddress) {
                await queryClient.invalidateQueries({
                    queryKey: blockchainQueryKeys.accountInstalledApps(aaAddress),
                })
            }
        },
        onSuccess: () => {
            navigate(`/${PATHS.MESSAGES}/new?to=${bot.clientAddress}&appAddress=${bot.appAddress}`)
        },
    })

    useEffect(() => {
        if (isAuthenticated && hasPendingInstall) {
            setShowInstallDialog(true)
            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev)
                newParams.delete(INSTALL_BOT_PARAM)
                return newParams
            })
        }
    }, [isAuthenticated, hasPendingInstall, setSearchParams])

    const handleUseBotClick = () => {
        if (isBotUnavailable) {
            return
        }
        setShowInstallDialog(true)
    }

    const handleLoginClick = useCallback(() => {
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev)
            newParams.set(INSTALL_BOT_PARAM, 'true')
            return newParams
        })
    }, [setSearchParams])

    const handleMouseEnter = useCallback(() => {
        prefetchBotStatus(bot.clientAddress)
    }, [prefetchBotStatus, bot.clientAddress])

    const handleCloseDialog = useCallback(() => {
        setShowInstallDialog(false)
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev)
            newParams.delete(INSTALL_BOT_PARAM)
            return newParams
        })
    }, [setSearchParams])

    return (
        <>
            <Stack
                width="100%"
                position="absolute"
                bottom="none"
                borderTop="default"
                background="backdropBlur"
                style={{
                    zIndex: 10,
                    left: 0,
                    right: 0,
                    pointerEvents: 'none',
                }}
            >
                <Stack
                    horizontal
                    paddingX
                    centerContent
                    width="100%"
                    alignItems="center"
                    paddingBottom="safeAreaInsetBottom"
                    minHeight="x12"
                >
                    <Stack
                        direction={{ desktop: 'row', mobile: 'column' }}
                        alignItems="center"
                        minHeight="x12"
                        gap={{ desktop: 'x4', mobile: 'sm' }}
                        width="100%"
                        maxWidth="1000"
                        paddingY={{ mobile: 'x2', default: undefined }}
                    >
                        {/* Desktop: messages on left */}
                        <Stack
                            grow
                            display={{ desktop: 'flex', mobile: 'none' }}
                            style={{ maxWidth: 650 }}
                        >
                            {isBotDisabled && (
                                <Paragraph color="gray2">This bot has been deactivated.</Paragraph>
                            )}
                            {cannotUseBot && (
                                <Paragraph color="gray2">
                                    You need to own a town to install this bot.
                                </Paragraph>
                            )}
                        </Stack>

                        {/* Button - centered on mobile, right-aligned on desktop */}
                        <Stack
                            width="100%"
                            pointerEvents="auto"
                            alignItems={{ desktop: 'end', mobile: 'center' }}
                        >
                            {isAuthenticated ? (
                                <FancyButton
                                    width={{ desktop: undefined, mobile: '100%' }}
                                    minWidth="200"
                                    disabled={isBotUnavailable}
                                    background="botGradient"
                                    borderRadius="full"
                                    onClick={handleUseBotClick}
                                    onMouseEnter={isBotUnavailable ? handleMouseEnter : undefined}
                                >
                                    {buttonText}
                                </FancyButton>
                            ) : (
                                <LoginComponent
                                    width={{ default: '300', mobile: '100%' }}
                                    disabled={isBotDisabled}
                                    cta={false}
                                    background="botGradient"
                                    borderRadius="full"
                                    text={buttonText}
                                    onLoginClick={handleLoginClick}
                                />
                            )}
                        </Stack>

                        {/* Mobile: messages below button */}
                        <Stack display={{ desktop: 'none', mobile: 'flex' }} alignItems="center">
                            {isBotDisabled && (
                                <Paragraph color="gray2" textAlign="center">
                                    This bot has been deactivated.
                                </Paragraph>
                            )}
                            {cannotUseBot && (
                                <Paragraph color="gray2" textAlign="center">
                                    You need to own a town to install this bot.
                                </Paragraph>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>

            {bot && aaAddress && userOwnsAnySpaces && (
                <InstallBotDialog
                    isOpen={showInstallDialog}
                    bot={bot}
                    target="space"
                    onClose={handleCloseDialog}
                />
            )}
            {bot && aaAddress && !userOwnsAnySpaces && enableBotDm && (
                <InstallBotDialog
                    isOpen={showInstallDialog}
                    bot={bot}
                    target="user"
                    walletAddress={aaAddress}
                    isNavigating={isNavigatingToDm}
                    onSuccess={navigateToDm}
                    onClose={handleCloseDialog}
                />
            )}
        </>
    )
}
